import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { optimizeHeroImage } from "../../utils/optimizeImage";

interface HeroSectionProps {
  heading: string;
  subheading: string;
  paragraph: string;
  images: string[];
}

export default function HeroSection({
  heading,
  subheading,
  paragraph,
  images,
}: HeroSectionProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate images every 8 seconds
  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [images.length]);

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentImageIndex(index);
  };

  const scrollToAbout = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Slideshow */}
      <div className="absolute inset-0 z-0">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 ${
              index === currentImageIndex ? "opacity-100" : "opacity-0"
            }`}
            style={{
              transition: "opacity 3s ease-in-out",
            }}
          >
            <img
              src={optimizeHeroImage(image)}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-cover"
              width={1920}
              height={1080}
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-black/40 z-10" />
      </div>

      {/* Content */}
      <div className="relative z-20 h-full flex items-center justify-center">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
            {heading}
          </h1>
          {subheading && (
            <h2 className="text-xl md:text-2xl lg:text-3xl text-white mb-6 drop-shadow-lg">
              {subheading}
            </h2>
          )}
          {paragraph && (
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-8 drop-shadow-lg">
              {paragraph}
            </p>
          )}

          <button
            onClick={scrollToAbout}
            className="px-8 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover transition-colors shadow-lg"
          >
            Explore Our Impact
          </button>
        </div>
      </div>

      {/* Slideshow Controls */}
      {images.length > 1 && (
        <>
          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 bg-black/30 hover:bg-black/50 text-white rounded-full transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 bg-black/30 hover:bg-black/50 text-white rounded-full transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight size={24} />
          </button>

          {/* Dot Indicators - with proper touch target size (min 44px) */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-1">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="p-3 flex items-center justify-center"
                aria-label={`Go to slide ${index + 1}`}
              >
                <span
                  className={`block rounded-full transition-all ${
                    index === currentImageIndex
                      ? "bg-white w-8 h-3"
                      : "bg-white/50 hover:bg-white/75 w-3 h-3"
                  }`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
